import { useState, useCallback, useEffect } from 'react'
import config from '@/lib/config'
import { formatCoins, formatCoinsLabel } from '@/lib/coins'

function getGuestId() {
  let id = localStorage.getItem('tv_guest_id')
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem('tv_guest_id', id)
  }
  return id
}

function arcadeHeaders(playMode) {
  const headers = { 'Content-Type': 'application/json', 'X-Play-Mode': playMode }
  if (playMode === 'demo') headers['X-Guest-ID'] = getGuestId()
  return headers
}

function arcadeFetch(playMode, path, body) {
  return fetch(`${config.backendUrl}/arcade/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: arcadeHeaders(playMode),
    body: JSON.stringify(body),
  }).then(r => r.json())
}

const GRID_SIZE = 25
const MINE_OPTIONS = [1, 3, 5, 10, 15, 20, 24]

const IDLE = 'idle'
const SAFE = 'safe'
const MINE_REVEAL = 'mine'

export default function TvMines({ onBalanceChange, playMode = 'demo' }) {
  const [balance, setBalance] = useState(null)
  const [bet, setBet] = useState('1.00')
  const [mineCount, setMineCount] = useState(3)
  const [phase, setPhase] = useState('idle')
  const [tiles, setTiles] = useState(Array(GRID_SIZE).fill(IDLE))
  const [gameId, setGameId] = useState(null)
  const [multiplier, setMultiplier] = useState(1)
  const [safeCount, setSafeCount] = useState(0)
  const [msg, setMsg] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setMsg('')
    fetch(`${config.backendUrl}/arcade/balance`, {
      credentials: 'include',
      headers: arcadeHeaders(playMode),
    }).then(r => r.json()).then(d => {
      if (d.success) setBalance(d.data.balance)
      else if (playMode === 'account') setMsg('Log in to play with your saved balance, or use Free Play.')
    }).catch(() => {})
  }, [playMode])

  const updateBalance = useCallback((b) => {
    setBalance(b)
    onBalanceChange?.(b)
  }, [onBalanceChange])

  const startGame = async () => {
    setLoading(true)
    setMsg('')
    const betNum = parseFloat(bet)
    if (isNaN(betNum) || betNum < 0.01) {
      setMsg('Invalid bet')
      setLoading(false)
      return
    }

    const res = await arcadeFetch(playMode, 'mines/start', { bet: betNum, mineCount })
    if (!res.success) {
      setMsg(playMode === 'account' && res.error === 'Not authenticated'
        ? 'Log in to play with your saved balance, or use Free Play.'
        : res.error)
      setLoading(false)
      return
    }

    setGameId(res.data.gameId)
    updateBalance(res.data.balance)
    setTiles(Array(GRID_SIZE).fill(IDLE))
    setMultiplier(1)
    setSafeCount(0)
    setPhase('playing')
    setLoading(false)
  }

  const pickTile = async (index) => {
    if (phase !== 'playing' || tiles[index] !== IDLE || loading) return
    setLoading(true)

    const res = await arcadeFetch(playMode, 'mines/pick', { gameId, tile: index })
    if (!res.success) {
      setMsg(res.error)
      setLoading(false)
      return
    }

    const newTiles = [...tiles]
    if (res.data.isMine) {
      res.data.mines.forEach(i => { newTiles[i] = MINE_REVEAL })
      newTiles[index] = MINE_REVEAL
      setTiles(newTiles)
      setPhase('lost')
      updateBalance(res.data.balance)
      setMsg('Boom. You hit a mine.')
    } else {
      newTiles[index] = SAFE
      setTiles(newTiles)
      setMultiplier(res.data.multiplier)
      setSafeCount(res.data.safeCount)
    }
    setLoading(false)
  }

  const cashout = async () => {
    if (phase !== 'playing' || safeCount === 0 || loading) return
    setLoading(true)

    const res = await arcadeFetch(playMode, 'mines/cashout', { gameId })
    if (!res.success) {
      setMsg(res.error)
      setLoading(false)
      return
    }

    const newTiles = [...tiles]
    res.data.mines.forEach(i => { if (newTiles[i] === IDLE) newTiles[i] = MINE_REVEAL })
    setTiles(newTiles)
    setPhase('won')
    updateBalance(res.data.balance)
    setMsg(`Cashed out at ${res.data.multiplier}x - won ${formatCoinsLabel(res.data.payout)}.`)
    setLoading(false)
  }

  const reset = () => {
    setPhase('idle')
    setTiles(Array(GRID_SIZE).fill(IDLE))
    setGameId(null)
    setMultiplier(1)
    setSafeCount(0)
    setMsg('')
  }

  const potentialWin = phase === 'playing' && safeCount > 0
    ? (parseFloat(bet) * multiplier).toFixed(2)
    : null

  return (
    <div className="w-full max-w-2xl mx-auto select-none">
      <h1 className="text-yellow-400 text-2xl font-bold mb-2 text-center tracking-wider">MINES</h1>
      <div className="text-center text-gray-400 text-sm mb-4">
        {playMode === 'demo' ? 'Free Play' : 'Saved Balance'}
      </div>

      {balance !== null && (
        <div className="text-center text-gray-400 text-sm mb-4">
          Balance: <span className="text-yellow-400 font-bold">{formatCoinsLabel(balance)}</span>
        </div>
      )}

      <div className="bg-[#1a1d29] rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-gray-400 text-xs uppercase tracking-wide">Bet (Coins)</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={bet}
            onChange={e => setBet(e.target.value)}
            disabled={phase === 'playing'}
            className="bg-[#0f1117] text-white border border-gray-700 rounded-lg px-3 py-2 w-28 text-sm focus:border-yellow-500 focus:outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-gray-400 text-xs uppercase tracking-wide">Mines</label>
          <select
            value={mineCount}
            onChange={e => setMineCount(parseInt(e.target.value))}
            disabled={phase === 'playing'}
            className="bg-[#0f1117] text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:border-yellow-500 focus:outline-none disabled:opacity-50"
          >
            {MINE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex gap-2 ml-auto">
          {phase === 'idle' || phase === 'won' || phase === 'lost' ? (
            <button
              onClick={phase === 'idle' ? startGame : reset}
              disabled={loading}
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-6 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {loading ? '...' : phase === 'idle' ? 'Play' : 'New Game'}
            </button>
          ) : (
            <button
              onClick={cashout}
              disabled={safeCount === 0 || loading}
              className="bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
            >
              {loading ? '...' : `Cash Out${potentialWin ? ` ${formatCoins(potentialWin)} Coins` : ''}`}
            </button>
          )}
        </div>
      </div>

      {phase === 'playing' && safeCount > 0 && (
        <div className="text-center mb-3">
          <span className="text-yellow-400 font-bold text-xl">{multiplier}x</span>
          <span className="text-gray-400 text-sm ml-2">({safeCount} safe)</span>
        </div>
      )}

      {msg && (
        <div className={`text-center text-sm mb-3 font-medium ${phase === 'won' ? 'text-green-400' : phase === 'lost' ? 'text-red-400' : 'text-gray-300'}`}>
          {msg}
        </div>
      )}

      <div className="grid grid-cols-5 gap-2">
        {tiles.map((state, i) => (
          <button
            key={i}
            onClick={() => pickTile(i)}
            disabled={phase !== 'playing' || state !== IDLE || loading}
            className={`
              aspect-square rounded-xl text-2xl flex items-center justify-center border transition-all font-bold
              ${state === IDLE && phase === 'playing'
                ? 'bg-[#1a1d29] border-gray-600 hover:border-yellow-500 hover:bg-[#252836] cursor-pointer'
                : state === SAFE
                  ? 'bg-green-900/60 border-green-500 cursor-default'
                  : state === MINE_REVEAL
                    ? 'bg-red-900/60 border-red-500 cursor-default'
                    : 'bg-[#1a1d29] border-gray-700 cursor-default opacity-60'
              }
            `}
          >
            {state === SAFE ? 'OK' : state === MINE_REVEAL ? 'X' : ''}
          </button>
        ))}
      </div>

      <p className="text-center text-xs text-gray-600 mt-4">
        {playMode === 'demo' ? 'Free-play coins only. No cash value.' : 'Playing with your saved coin balance.'}
      </p>
    </div>
  )
}
